import { Component, Event, EventEmitter, Listen, Prop, State, h } from '@stencil/core';
import { Address } from '../../../globals/address';
import { Customer } from '../../../globals/customer';
import { numSsnCharsShown } from '../../../globals/globals';
import { PageKey } from '../../../globals/page-key';
import { apiDateFormatter, objectHasValues, ErrorHelper } from '../../../helpers';
import { updateLeaseApplication } from '../../../utils';
import { PersonalInfoTranslation } from '../../../globals/translations';

@Component({
  tag: 'personal-info-state',
  styleUrl: './state-styles/personal-info-state.scss',
  shadow: false,
})
export class PersonalInfoState {
  @State() isLoading = false;

  @Prop({ mutable: true }) personalInfo: Customer;
  @Prop({ mutable: true }) errors: ErrorHelper;

  @Prop() addressInfo: Address;
  @Prop() redirectToReview: boolean;
  @Prop() addressNotPrefilled: boolean;
  @Prop() translation: Partial<PersonalInfoTranslation>;

  @Event() updateCustomer: EventEmitter<Partial<Customer>>;
  @Event() setView: EventEmitter<PageKey>;
  @Event() continueClick: EventEmitter;

  @Listen('updateIdentityInfo', { target: 'body' })
  async getUpdatedIdentity(event: CustomEvent) {
    if (event.detail) {
      const { firstName, lastName, dob, ssn } = event.detail;

      const toUpdate = {
        firstName: this.personalInfo.firstName,
        lastName: this.personalInfo.lastName,
      } as Partial<Customer>;

      if (firstName !== this.personalInfo.firstName) {
        toUpdate.firstName = firstName;
        this.personalInfo.firstName = firstName;
      }

      if (lastName !== this.personalInfo.lastName) {
        toUpdate.lastName = lastName;
        this.personalInfo.lastName = lastName;
      }

      if (ssn !== this.personalInfo.taxpayerId) {
        toUpdate.taxpayerId = ssn.replaceAll('-', '');
        this.personalInfo.taxpayerId = `****-**-${ssn.substr(ssn.length - numSsnCharsShown)}`;
      }

      if (dob !== this.personalInfo.dateOfBirth) {
        toUpdate.dateOfBirth = apiDateFormatter(dob);
        this.personalInfo.dateOfBirth = dob;
      }

      if (!objectHasValues(this.personalInfo)) {
        return;
      }

      try {
        this.isLoading = true;
        await updateLeaseApplication(toUpdate);
        this.updateCustomer.emit({ ...this.personalInfo });
      } catch (error) {
        this.errors.hasGenericError = true;
        this.isLoading = false;

        return;
      }

      if (!this.redirectToReview) {
        if (this.addressNotPrefilled) {
          return this.setView.emit(PageKey.addressInfo);
        }

        const address = { shipping: this.addressInfo, billing: this.addressInfo };

        try {
          this.isLoading = true;
          await updateLeaseApplication({ address });
          this.updateCustomer.emit({ address });
          this.continueClick.emit();
        } catch (error) {
          this.errors.hasGenericError = true;
        }
        this.isLoading = false;
      }
    }
  }

  render() {
    return (
      <pc-personal-info {...this.personalInfo} isLoading={this.isLoading} translation={this.translation}>
        {this.errors.hasGenericError && <pc-generic-error slot="error-card" />}
      </pc-personal-info>
    );
  }
}
